import { createContext, useContext, useState, type ReactNode } from "react";
import { Link } from "wouter";
import { Check, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { ToolContainer } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { getOrCreateDeviceId } from "@/lib/deviceId";
import { getLoginUrlForWorkflow } from "@/const";
import { workflowCtaKind, workflowCtaLabel } from "@/lib/workflow/workflowCta";
import { useLocale } from "@/hooks/useLocale";
import { PLAN_CARDS, planPriceLine, TRIAL_DAYS, type SuitePlan } from "@/lib/workflow/suitePlans";
import { WorkflowPreviewBlock } from "@/components/WorkflowPreviewBlock";
import { EVENT_SUITE_TRY_FIRST } from "@/lib/workflow/workflowPlans";
import { ALL_PRODUCT_IDS } from "@/lib/workflow/suites";
import { getToolById } from "@/lib/tools-data";
import { getConnectedApp } from "@/lib/workflow/connectedApps";
import { premiumNotifyMailto } from "@/lib/workflow/notifyMailto";
import { NoticeBox } from "@/components/NoticeBox";

export interface SuiteAccess { entitled: boolean; plan: SuitePlan | null; premium: boolean }
export const PaidSuiteContext = createContext<SuiteAccess>({ entitled: false, plan: null, premium: false });

/** True inside the paid Event Suite once access has been confirmed: the daily trial count does not apply there. */
export const usePaidSuite = () => useContext(PaidSuiteContext).entitled;
/** The plan the person has inside the suite (Premium unlocks storage and messaging). */
export const useSuiteAccess = () => useContext(PaidSuiteContext);

const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "");

/**
 * Wraps the Event Suite and its products. Access is a monthly subscription (Standard or Premium, with a 7-day
 * trial the first time), or a seat in an organization plan. Everything else on the site is unaffected.
 */
export function EventSuiteGate({ children, name = "Event Suite" }: { children: ReactNode; name?: string }) {
  const { isAuthenticated } = useAuth();
  const { localizedHref } = useLocale();
  const deviceId = isAuthenticated ? undefined : getOrCreateDeviceId();
  const utils = trpc.useUtils();
  const status = trpc.eventSuite.status.useQuery({ deviceId });
  const checkout = trpc.eventSuite.checkout.useMutation();
  const change = trpc.eventSuite.changePlan.useMutation();
  const portal = trpc.eventSuite.portal.useMutation();
  const [busy, setBusy] = useState<SuitePlan | null>(null);

  const subscribe = async (plan: SuitePlan) => {
    if (!isAuthenticated) {
      // Sign in, come back here, and useAutoResumeCheckout (mounted in Layout) opens Stripe Checkout for this plan.
      window.location.href = getLoginUrlForWorkflow("event-suite", plan);
      return;
    }
    setBusy(plan);
    try {
      const r = await checkout.mutateAsync({ plan });
      window.location.href = r.url;
    } catch (e) {
      setBusy(null);
      toast.error(e instanceof Error ? e.message : "Could not start checkout. Please try again.");
    }
  };

  const manage = async () => {
    try {
      const r = await portal.mutateAsync();
      window.location.href = r.url;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not open billing. Please try again.");
    }
  };

  const switchPlan = async (plan: SuitePlan) => {
    setBusy(plan);
    try {
      await change.mutateAsync({ plan });
      toast.success(`Switched to ${PLAN_CARDS[plan].name}. It can take a moment to show.`);
      setTimeout(() => void utils.eventSuite.status.invalidate(), 2500);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not change plan. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  if (status.isLoading) {
    return <ToolContainer><div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground" role="status"><Loader2 className="h-4 w-4 animate-spin" />Checking your access</div></ToolContainer>;
  }

  const d = status.data;
  if (d?.entitled) {
    const plan = (d.plan ?? "standard") as SuitePlan;
    return (
      <PaidSuiteContext.Provider value={{ entitled: true, plan, premium: d.premium }}>
        {children}
        {d.hasSubscription && (
          <div className="mx-auto max-w-3xl px-4 pb-6 text-center text-xs text-muted-foreground space-y-1" data-testid="suite-account">
            <p>
              {PLAN_CARDS[plan].name}{d.trialing ? `, trial until ${fmtDate(d.until)}` : `, renews ${fmtDate(d.until)}`}.{" "}
              <button type="button" className="underline" onClick={() => void manage()} data-testid="suite-manage">Manage or cancel</button>
              {plan === "standard" && d.premiumOpen && <> · <button type="button" className="underline" disabled={busy !== null} onClick={() => void switchPlan("premium")} data-testid="suite-upgrade">Upgrade to Premium</button></>}
              {plan === "premium" && <> · <button type="button" className="underline" disabled={busy !== null} onClick={() => void switchPlan("standard")}>Switch to Event Suite</button></>}
            </p>
            {d.endsAtPeriodEnd
              ? <NoticeBox role="status" testId="suite-ends">Cancelled. Your plan ends on {fmtDate(d.until)}, and anything you saved to your account is erased then. Download it before that day.</NoticeBox>
              : <p>If you cancel, your plan and stored data continue to {fmtDate(d.until)}, then stored data is erased.</p>}
          </div>
        )}
      </PaidSuiteContext.Provider>
    );
  }

  const trial = d?.trialEligible ?? true;
  const premiumOpen = d?.premiumOpen ?? false;
  return (
    <ToolContainer>
      <section className="mx-auto max-w-3xl" data-testid="suite-paywall">
        <div className="text-center">
          <Lock className="mx-auto h-6 w-6 text-primary" aria-hidden />
          <h2 className="mt-2 text-xl font-semibold">{name}</h2>
          <p className="mt-1 text-sm text-muted-foreground">Plan any event from first quote to final report.{trial ? ` Try it for ${TRIAL_DAYS} days first.` : ""}</p>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {(Object.keys(PLAN_CARDS) as SuitePlan[]).map((plan) => {
            const c = PLAN_CARDS[plan];
            return (
              <div key={plan} className={`rounded-xl border bg-card p-5 flex flex-col ${plan === "premium" ? "border-primary" : ""}`} data-testid={`plan-${plan}`}>
                <h3 className="font-semibold">{c.name}{plan === "premium" && !premiumOpen ? <span className="ml-2 rounded bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">Opens soon</span> : null}</h3>
                <p className="mt-2 text-3xl font-bold">${c.monthlyUsd}<span className="text-base font-normal text-muted-foreground"> / month</span></p>
                <p className="text-xs text-muted-foreground">{planPriceLine(plan)}</p>
                <ul className="mt-3 space-y-2 text-sm flex-1">
                  {c.features.map((t) => <li key={t} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden /><span>{t}</span></li>)}
                </ul>
                {plan === "premium" && !premiumOpen ? (
                  <Button asChild className="mt-4 w-full gap-2" variant="outline" data-testid="notify-premium">
                    <a href={premiumNotifyMailto()}>Tell me when it opens</a>
                  </Button>
                ) : (
                  <Button className="mt-4 w-full gap-2" variant={plan === "premium" ? "default" : "outline"} onClick={() => void subscribe(plan)} disabled={busy !== null} data-testid={`subscribe-${plan}`}>
                    {busy === plan && <Loader2 className="h-4 w-4 animate-spin" />}
                    {workflowCtaLabel(workflowCtaKind({ signedIn: isAuthenticated, entitled: false, trialEligible: trial }), c.monthlyUsd)}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
        <WorkflowPreviewBlock
          testId="wf-preview-event-suite"
          deliverables={[
            "Guest list CSV, name badges with QR passes, invitation cards, calendar invites (.ics) and an event web page",
            "Printed agenda, crew cue sheet, seating plan, floor layout drawing and place cards",
            "Budget plan, profit statement, quote comparison, purchase orders and certificates",
            "Event closure report (PDF) at the end",
            "Your project can be backed up as a file and restored later",
          ]}
          tryFirst={EVENT_SUITE_TRY_FIRST}
        />
        <section className="mt-5 rounded-xl border bg-card p-5" data-testid="suite-thirteen" aria-labelledby="suite-thirteen-h">
          <h3 id="suite-thirteen-h" className="font-semibold">The thirteen products inside</h3>
          <ul className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            {ALL_PRODUCT_IDS.map((id) => {
              const t = getToolById(id);
              const outputs = getConnectedApp(id)?.outputs;
              return (
                <li key={id}>
                  {t ? <Link href={localizedHref(`/tool/${id}`)} className="font-medium text-primary underline-offset-2 hover:underline">{t.name}</Link> : <span className="font-medium">{id}</span>}
                  {outputs && <span className="block text-xs text-muted-foreground">{outputs.slice(0, 3).join(", ")}</span>}
                </li>
              );
            })}
          </ul>
        </section>
        <p className="mt-4 text-xs text-muted-foreground text-center" data-testid="suite-terms">
          {trial ? `After the ${TRIAL_DAYS}-day trial you are charged monthly unless you cancel first. ` : ""}Billed monthly. If you cancel, your plan and anything you saved to your account continue to the end of the paid month, then saved data is erased. No trial uses and no day pass.
        </p>
        <p className="mt-2 text-xs text-muted-foreground text-center">
          Several people? An <Link href={localizedHref("/pricing")} className="underline">organization plan</Link> covers a whole team. If yours has one, sign in with your work email.
        </p>
      </section>
    </ToolContainer>
  );
}
